import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import ToolButton, { ToolButtonType } from '../../../tools/tool-button';
import { t } from 'i18next';
import '../../../App.css';
import './new-session-panel.css';
import SearchComputeSiteList from '../../search-compute/search-compute-site-list';
import SearchComputeServiceTypesList from '../../search-compute/search-compute-service-types-list';
import { APIPrefix } from '../../../utils/functions';
import { ServiceDetails, ComputeServiceType, Site } from '../../../utils/types';
import { computeSessionDurationOptions } from '../../../utils/common/constants';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';

// classes
import ToolSmall from '../../../tools/tool-small';
import PleaseWait from '../../../tools/please-wait';

// import types.
import { CanfarServiceType } from '../../../utils/types';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  displaySession,
  refreshSRCNetServices,
  refreshCanfarServices,
} from '../../interactive-sessions/interactive-sessions-actions';
import {
  addSessions,
  removeSessions,
} from '../../interactive-sessions/interactive-sessions-slice';
import { useTranslation } from 'react-i18next';

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

const NewSessionPanel: React.FC<{
  onClose: () => void;
  site_uuid?: string;
  service_uuid?: string;
  service_type?: string;
}> = (props: {
  onClose: () => void;
  site_uuid?: string;
  service_uuid?: string;
  service_type?: string;
}) => {
  const { t } = useTranslation();
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_LoggedInRefreshTrigger: number | undefined = useAppSelector(
    (state) => state.global.loggedInRefreshTrigger,
  );
  const sG_SiteCapabilitiesTokenObtained: boolean | undefined = useAppSelector(
    (state) => state.global.siteCapabilitiesTokenObtained,
  );

  // data state variables.
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_canfarServices: CanfarServiceType[] = useAppSelector(
    (state) => state.data.canfarServices ?? [],
  );
  const sD_computeServices: ComputeServiceType[] = useAppSelector(
    (state) => state.data.computeServices,
  );

  // hold the list of compute services from the site-capabilities catalog.
  const sSessions: ComputeServiceType[] = useAppSelector(
    (state) => state.interactiveSessions.sessions,
  );

  // Get translated options
  const sessionDurationOptions = computeSessionDurationOptions(t);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const UNDEFINED_SITE: Site = {};

  // find the default node and site, if one is supplied.
  let NODE: string = '';
  let SITE_UUID: string = '';
  let SERVICE_TYPE: string = '';
  let SERVICE_UUID: string = '';
  let PAGE: string = '';
  if (props.site_uuid !== undefined) {
    const site: Site | undefined = sD_sites.find(
      (site) => site.site_uuid === props.site_uuid,
    );
    if (site !== undefined) {
      NODE = site.node ?? '';
      SITE_UUID = props.site_uuid;
    }
  }

  if (props.service_uuid !== undefined) {
    const service: ComputeServiceType | undefined = sD_computeServices.find(
      (service) => service.compute_service_id === props.service_uuid,
    );
    if (service !== undefined) {
      NODE = service.site.node ?? '';
      SITE_UUID = service.site.site_uuid ?? '';
      SERVICE_TYPE = service.session_type;
      SERVICE_UUID = service.compute_service_id;
    }
    PAGE = 'srcnet';
  }

  if (props.service_type !== undefined) {
    SERVICE_TYPE = props.service_type;
    PAGE = 'canfar';
  }

  // select boxes.
  const [sNodeValue, setNodeValue] = useState<string>(NODE);
  const [sSiteValue, setSiteValue] = useState<string>(SITE_UUID);
  const [sServiceType, setServiceType] = useState<string>(SERVICE_TYPE);
  const [sDuration, setDuration] = useState<string>('');
  const [sComputeService, setComputeService] = useState<string>(SERVICE_UUID);
  const [sImage, setImage] = useState<string>('');
  const [sCores, setCores] = useState<string>('');
  const [sRam, setRam] = useState<string>('');

  // select box options.
  const [sCoreOptions, setCoreOptions] = useState<string[]>([]);
  const [sRamOptions, setRamOptions] = useState<string[]>([]);

  // input boxes.
  const [sCmd, setCmd] = useState<string>('');
  const [sArgs, setArgs] = useState<string>('');
  const [sEnv, setEnv] = useState<string>('');
  const [sName, setName] = useState<string>('');

  // the canfar service of the selected site.
  const [sCanfarService, setCanfarService] = useState<CanfarServiceType | undefined>(
    undefined,
  );

  const [sLoadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [sErrorMessage, setErrorMessage] = useState<string>('');

  const [sPage, setPage] = useState<string>(PAGE !== '' ? PAGE : 'srcnet');

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Restrict input box entry to alphanumeric and hyphen only.
  //
  //	------------------------------------------------------------

  const alphanumericKeysOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
    //
    const key: number = event.charCode;
    const validChar: boolean =
      (key >= 48 && key <= 57) ||
      (key >= 65 && key <= 90) ||
      (key >= 97 && key <= 122) ||
      key === 45;
    if (validChar === false) {
      event.preventDefault();
    }
    //
  }; // alphanumericKeysOnly

  //	-------------------------------------------------
  //
  //	extract a unique list of nodes from the list
  //	of nodes and sites.
  //
  //	-------------------------------------------------

  function getNodes(args: { sites: Site[] }) {
    //
    let nodes: string[] = [];

    for (let i: number = 0; i < args.sites.length; i++) {
      //
      if (nodes.indexOf(args.sites[i].node ?? '') === -1) {
        nodes.push(args.sites[i].node ?? '');
      }
      //
    }

    // return something.
    return nodes;
    //
  } // getNodes

  //	-------------------------------------------------
  //
  //	get a unique list of service types from the
  //	compute services array.
  //
  //	-------------------------------------------------

  function getServiceTypes(args: { node: string; site_uuid: string }) {
    //
    let service_types: string[] = [];

    if (sPage === 'srcnet') {
      //
      for (let i: number = 0; i < sD_computeServices.length; i++) {
        //
        if (
          (sD_computeServices[i].site.node === args.node || args.node === '') &&
          (sD_computeServices[i].site.site_uuid === args.site_uuid ||
            args.site_uuid === '')
        ) {
          if (
            service_types.indexOf(sD_computeServices[i].session_type) === -1 &&
            ['jupyterhub', 'carta'].indexOf(sD_computeServices[i].session_type) > -1
          ) {
            service_types.push(sD_computeServices[i].session_type);
          }
        }
        //
      }
      //
    } else {
      //
      for (let i: number = 0; i < sSessions.length; i++) {
        //
        if (
          (sSessions[i].site.node === args.node || args.node === '') &&
          (sSessions[i].site.site_uuid === args.site_uuid || args.site_uuid === '') &&
          sSessions[i].resource_type === 'canfar' &&
          sSessions[i].compute_status === 'available'
        ) {
          if (service_types.indexOf(sSessions[i].session_type) === -1) {
            service_types.push(sSessions[i].session_type);
          }
        }
        //
      }
      //
    }

    // return something.
    return service_types;
    //
  } // getServiceTypes

  //	-------------------------------------------------
  //
  //	extract a unique list of sites from the list
  //	of nodes and sites, and a given node.
  //
  //	-------------------------------------------------

  function getSites(args: { node: string }) {
    //
    let sites: Site[] = [];

    for (let i: number = 0; i < sD_sites.length; i++) {
      //
      if (sD_sites[i].node === args.node) {
        if (
          sites.findIndex((item) => item.site_uuid === sD_sites[i].site_uuid ?? '') === -1
        ) {
          sites.push(sD_sites[i]);
        }
      }
      //
    }

    // return something.
    return sites;
    //
  } // getSites

  //	-------------------------------------------------
  //
  //	function that handles a launch of a Compute Service
  //
  //	-------------------------------------------------
  const handleLaunch = async () => {
    //
    setErrorMessage('');
    let selectedService: ComputeServiceType | undefined = undefined;

    // srcnet-specific validation.
    if (sPage === 'srcnet') {
      //
      if (sComputeService === '') {
        setErrorMessage('Please select a service before launching');
        return;
      }

      selectedService = sD_computeServices.find(
        (service) => service.compute_service_id === sComputeService,
      );
      if (selectedService === undefined) {
        setErrorMessage('Selected compute service is invalid');
        return;
      }
      //
    }

    // canfar-specific validation.
    if (sPage === 'canfar') {
      //
      if (sImage === '') {
        setErrorMessage('Please select an image');
        return;
      }

      if (sName.trim() === '') {
        setErrorMessage('Please enter a session name');
        return;
      }
      //
    }

    // Closing the modal after the launch button is pressed.
    props.onClose();

    // launch an SRCNet service.
    if (sPage === 'srcnet') {
      //
      const payload = {
        compute_service_id: sComputeService,
        notebook_path: '/user/home',
        is_embedded: selectedService?.is_embedded ?? false,
        duration_days: sDuration ? parseInt(sDuration) : 2,
      };

      // add a temporary record to the list of compute services.
      // it will be removed once the process has completed.
      const tempId = Date.now();
      let newSession: ComputeServiceType = Object.assign({}, selectedService);
      newSession.id = tempId;
      newSession.compute_status = 'pending';
      newSession.overridden_status = 'starting';
      dispatch(addSessions([newSession]));

      try {
        const response = await fetch(APIPrefix() + '/v1/launch_compute_session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (response.status === 409) {
          dispatch(
            removeSessions({
              id: [tempId],
              resource_type: ['srcnet'],
            }),
          );
          setErrorMessage(result.detail);
        } else if (!response.ok) {
          dispatch(
            removeSessions({
              id: [tempId],
              resource_type: ['srcnet'],
            }),
          );
          setErrorMessage('An unexpected error occured. Please try again.');
          console.log('Launch failed ' + JSON.stringify(result));
        } else {
          //
          // Wait until real session with same compute_service_id and "running" appears
          let retries = 0;
          const maxRetries = 20;
          const interval = setInterval(async () => {
            //
            let isRunning: boolean = false;

            try {
              //
              const response = await fetch(
                APIPrefix() +
                  '/v1/active_compute_sessions/get_session?service_id=' +
                  sComputeService,
                {
                  method: 'GET',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  credentials: 'include',
                },
              );

              if (response.status === 200) {
                const service: object = await response.json();

                if ('compute_status' in service) {
                  isRunning = service.compute_status === 'running';
                }
              }
              //
            } catch (err) {}

            // open this session.
            if (isRunning === true) {
              let runningSession: ComputeServiceType = Object.assign({}, selectedService);
              runningSession.compute_status = 'running';
              displaySession({ session: runningSession });
            }

            if (isRunning === true || retries >= maxRetries) {
              //
              clearInterval(interval);

              // Refresh sessions.
              dispatch(refreshSRCNetServices());
              //
            }

            retries++;
            //
          }, 1000);
          //
        }
      } catch (err) {
        //
        // Remove the temporary launching session
        dispatch(
          removeSessions({
            id: [tempId],
            resource_type: ['srcnet'],
          }),
        );
        console.log('Launch err ', err);
        setErrorMessage('Something went wrong while launching.');
        //
      } finally {
      }
      //
    }

    // launch a canfar service.
    if (sPage === 'canfar') {
      //
      // get the site.
      const site: Site | undefined = sD_sites.find(
        (site) => site.site_uuid === sSiteValue,
      );

      if (sCanfarService !== undefined) {
        //
        // add a temporary record to the list of compute services.
        // it will be removed once the process has completed.
        const tempId = Date.now();
        let newSession: ComputeServiceType = {
          id: tempId,
          site: site ?? {
            node: sNodeValue,
            site: '',
            site_uuid: sSiteValue,
          },
          resource_type: 'canfar',
          session_type: sServiceType,
          compute_service_id: '',
          service_name: sName,
          service_url: '',
          is_embedded: true,
          compute_status: 'pending',
          overridden_status: 'starting',
          top_banner:
            '@ ' +
            (sCanfarService.name !== ''
              ? sCanfarService.name
              : 'unknown CANFAR instance'),
          active_status: true,
        };
        dispatch(addSessions([newSession]));

        try {
          //
          let query: string =
            APIPrefix() +
            '/v1/skaha/session?' +
            'url=' +
            encodeURIComponent(sCanfarService.url) +
            '&' +
            'name=' +
            encodeURIComponent(sName) +
            '&' +
            'image=' +
            encodeURIComponent(sImage) +
            '&' +
            'cores=' +
            encodeURIComponent(sCores) +
            '&' +
            'ram=' +
            encodeURIComponent(sRam) +
            '&' +
            'type=' +
            encodeURIComponent(sServiceType) +
            '&';
          if (sServiceType === 'headless' && sCmd !== '') {
            query = query + 'cmd=' + encodeURIComponent(sCmd) + '&';
          }
          if (sServiceType === 'headless' && sArgs !== '') {
            query = query + 'args=' + encodeURIComponent(sArgs) + '&';
          }
          if (sServiceType === 'headless' && sEnv !== '') {
            query = query + 'env=' + encodeURIComponent(sEnv) + '&';
          }
          query = query.slice(0, -1);
          const response = await fetch(query, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          });

          const result = await response.json();

          if (response.status === 409) {
            dispatch(
              removeSessions({
                id: [tempId],
                resource_type: ['canfar'],
              }),
            );
            setErrorMessage(result.detail);
          } else if (!response.ok) {
            dispatch(
              removeSessions({
                id: [tempId],
                resource_type: ['canfar'],
              }),
            );
            setErrorMessage('An unexpected error occured. Please try again.');
            console.log('Launch failed ' + JSON.stringify(result));
          } else {
            //
            // Refresh sessions.
            dispatch(
              refreshCanfarServices({
                canfarService: sCanfarService,
                refreshAvailable: false,
              }),
            );
            //
          }
        } catch (err) {
          //
          // Remove the temporary launching session
          dispatch(
            removeSessions({
              id: [tempId],
              resource_type: ['canfar'],
            }),
          );
          console.log('Launch err ', err);
          setErrorMessage('Something went wrong while launching.');
          //
        } finally {
        }
        //
      }
      //
    }
    //
  }; // handleLaunch

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const isButtonLike =
      target?.tagName === 'BUTTON' || target?.getAttribute('role') === 'button';
    const isTextArea = target instanceof HTMLTextAreaElement;

    if (event.defaultPrevented) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      props.onClose();
      return;
    }

    if (event.key === 'Enter' && !isButtonLike && !isTextArea) {
      event.preventDefault();
      handleLaunch();
    }
  }; // handleFormKeyDown

  //	------------------------------------------------------------
  //
  //	Handler for select box onChange event.
  //
  //	------------------------------------------------------------

  const onChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //
    const inputBox = event.target;

    // update the state after a select component change.
    if (inputBox.name === 'lst-node') {
      //
      setNodeValue(inputBox.value);

      // check if there is only one site for this node.
      let numSites: number = 0;
      let site: Site = UNDEFINED_SITE;
      for (let i: number = 0; i < sD_sites.length; i++) {
        if (sD_sites[i].node === inputBox.value) {
          site = sD_sites[i];
          numSites = numSites + 1;
        }
      }
      if (numSites !== 1) {
        site = UNDEFINED_SITE;
      }
      setSiteValue(site.site_uuid ?? '');
      //
    }
    if (inputBox.name === 'lst-site') {
      setSiteValue(inputBox.value);
    }
    //
  }; // onChangeHandler

  //	-------------------------------------------------
  //
  //	handle clicks for btn component.
  //
  //	-------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // change the tab page.
    if (button.name === 'btn-srcnet' && PAGE === '') {
      setPage('srcnet');
    }
    if (button.name === 'btn-canfar' && PAGE === '') {
      setPage('canfar');
    }
    //
  }; // onClickBtnHandler

  //	-------------------------------------------------
  //
  //	get the list of skaha resource options, such
  //	as ram and cores.
  //
  //	-------------------------------------------------

  async function getSkahaResourceOptions(args: { canfarService: CanfarServiceType }) {
    //
    setLoadingOptions(true);
    try {
      //
      const response = await fetch(
        APIPrefix() + '/v1/skaha/resource_options?url=' + args.canfarService.url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        },
      );
      setLoadingOptions(false);

      if (response.status === 200) {
        //
        const options = await response.json();

        // get the list of cores.
        if ('cores' in options) {
          if ('default' in options.cores) {
            setCores(options.cores.default);
          }
          if ('options' in options.cores) {
            setCoreOptions(options.cores.options);
          }
        }

        // get the list of ram.
        if ('ram' in options) {
          if ('default' in options.ram) {
            setRam(options.ram.default);
          }
          if ('options' in options.ram) {
            setRamOptions(options.ram.options);
          }
        }
        //
      }
      //
    } catch (err) {}
    //
  } // getSkahaResourceOptions

  //	------------------------------------------------------------
  //
  //	H O O K S
  //
  //	------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	get the list of skaha resource options when the
  //	page loads, but only if the site is fixed. this
  //	is needed because the site hook doesn't fire
  //	when the site state is set on loading.
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    if (SITE_UUID !== '') {
      //
      // get the canfar instance for this site.
      let canfarService: CanfarServiceType | undefined = sD_canfarServices.find(
        (service) => service.site.site_uuid === SITE_UUID,
      );
      if (canfarService !== undefined) {
        //
        // get the skaha resource options when the site is changed.
        getSkahaResourceOptions({ canfarService: canfarService });
        //
      }

      // set the canfar instance.
      setCanfarService(canfarService);
      //
    }
    //
  }, []);

  //	-------------------------------------------------
  //
  //	get the list of skaha resource options when the
  //	site changes.
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    // get the canfar instance for this site.
    let canfarService: CanfarServiceType | undefined = sD_canfarServices.find(
      (service) => service.site.site_uuid === sSiteValue,
    );
    if (canfarService !== undefined) {
      //
      // get the skaha resource options when the site is changed.
      getSkahaResourceOptions({ canfarService: canfarService });
      //
    }

    // set the canfar instance.
    setCanfarService(canfarService);
    //
  }, [sSiteValue]);

  //	-------------------------------------------------
  //
  //	when the selected compute service changes
  //	then select the corresponding service type.
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    if (sComputeService !== '') {
      //
      let selectedComputeService: ComputeServiceType | undefined =
        sD_computeServices.find((find) => find.compute_service_id === sComputeService);
      if (selectedComputeService !== undefined) {
        setServiceType(selectedComputeService.session_type);
      }
      //
    }
    //
  }, [sComputeService]);

  //	-------------------------------------------------
  //
  //	Hook that dynamically clears the selected compute service
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    // check if the selected compute service is valid for the current site and service type.
    let valid: boolean = false;
    if (sComputeService !== '') {
      //
      let selectedComputeService: ComputeServiceType | undefined =
        sD_computeServices.find((find) => find.compute_service_id === sComputeService);
      if (selectedComputeService !== undefined) {
        valid =
          (selectedComputeService.site.site_uuid === sSiteValue || sSiteValue === '') &&
          (selectedComputeService.session_type === sServiceType || sServiceType === '');
      }
      //
    } else {
      valid = true;
    }

    if (valid === false) {
      setComputeService(SERVICE_UUID);
    }

    // check if the selected service type exists on any compute service for this site.
    valid = false;
    if (sServiceType !== '' && sSiteValue !== '') {
      valid =
        getServiceTypes({ node: sNodeValue, site_uuid: sSiteValue }).findIndex(
          (find) => find === sServiceType,
        ) > -1;
    } else {
      valid = true;
    }

    if (valid === false) {
      setServiceType(SERVICE_TYPE);
    }
    //
  }, [sSiteValue, sServiceType]);

  //	-------------------------------------------------
  //
  //	Handle the closing of the dialog.
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        props.onClose();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
    //
  }, [props.onClose]);

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div className="new-session-form" onKeyDownCapture={handleFormKeyDown}>
      {sErrorMessage && <div className="error-box">{sErrorMessage}</div>}

      {/* the srcnet and canfar tabs */}
      <div
        style={{ flex: '0 0 auto', width: '100%', display: 'flex', flexDirection: 'row' }}
      >
        <ToolSmall
          name="btn-srcnet"
          text="SRCNet"
          onClick={onClickBtnHandler}
          selected={sPage === 'srcnet'}
          disabled={PAGE !== '' && sPage !== 'srcnet'}
          left={true}
          right={false}
          tabWidth={232}
        />
        <ToolSmall
          name="btn-canfar"
          text="Canfar"
          onClick={onClickBtnHandler}
          selected={sPage === 'canfar'}
          disabled={PAGE !== '' && sPage !== 'canfar'}
          left={false}
          right={true}
          tabWidth={232}
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <div
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
          }}
        >
          {/* node filter box */}
          <label
            id="new-session-node-label"
            className="text-colour"
            style={{
              margin: '5px 0px 0px 0px',
            }}
          >
            Node:
          </label>
          <select
            aria-labelledby="new-session-node-label"
            name="lst-node"
            className="listbox"
            multiple={false}
            value={sNodeValue}
            onChange={onChangeHandler}
            data-placeholder-shown={sNodeValue === '' ? 'T' : 'F'}
            style={{ lineHeight: '100%', borderRadius: '0px' }}
            disabled={NODE !== ''}
            data-disabled={NODE !== '' ? 'T' : 'F'}
          >
            <option hidden label={t('Select node')} value="" />
            {getNodes({ sites: sD_sites }).map((node) => (
              <option key={node ?? ''} label={node ?? ''} value={node ?? ''} />
            ))}
          </select>

          {/* site filter box */}
          <label
            id="new-session-site-label"
            className="text-colour"
            style={{
              margin: '5px 0px 0px 0px',
            }}
          >
            Site:
          </label>
          <select
            aria-labelledby="new-session-site-label"
            name="lst-site"
            className="listbox"
            multiple={false}
            value={sSiteValue}
            onChange={onChangeHandler}
            data-placeholder-shown={sSiteValue === '' ? 'T' : 'F'}
            style={{ lineHeight: '100%', borderRadius: '0px' }}
            disabled={SITE_UUID !== ''}
            data-disabled={SITE_UUID !== '' ? 'T' : 'F'}
          >
            <option hidden label={t('Select site')} value="" />
            {getSites({ node: sNodeValue }).map((item, index) => (
              <option key={index} label={item.site ?? ''} value={item.site_uuid} />
            ))}
          </select>
          <label
            id="new-session-service-type-label"
            className="text-colour"
            style={{
              margin: '5px 0px 0px 0px',
            }}
          >
            Service type:
          </label>
          <select
            aria-labelledby="new-session-session-service-type-label"
            name="lst-service-types"
            className="listbox"
            multiple={false}
            value={sServiceType}
            onChange={(e) => setServiceType(e.target.value)}
            data-placeholder-shown={
              sServiceType === '' ||
              getServiceTypes({ node: sNodeValue, site_uuid: sSiteValue }).indexOf(
                sServiceType,
              ) === -1
                ? 'T'
                : 'F'
            }
            style={{ lineHeight: '100%', borderRadius: '0px' }}
            disabled={SERVICE_TYPE !== ''}
            data-disabled={SERVICE_TYPE !== '' ? 'T' : 'F'}
          >
            <option hidden label={t('Select service type')} value="" />
            {getServiceTypes({ node: sNodeValue, site_uuid: sSiteValue }).map(
              (service_type) => (
                <option
                  key={service_type}
                  label={service_type.toUpperCase()}
                  value={service_type}
                />
              ),
            )}
          </select>

          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              gap: '5px',
              display: sPage === 'canfar' ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <label
              id="new-session-images-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Select image:
            </label>
            <select
              aria-labelledby="new-session-images-label"
              name="lst-images"
              className="listbox"
              multiple={false}
              value={sImage}
              onChange={(e) => setImage(e.target.value)}
              data-placeholder-shown={sImage === '' ? 'T' : 'F'}
              style={{ borderRadius: '0px' }}
              disabled={sNodeValue === '' || sSiteValue === ''}
              data-disabled={sNodeValue === '' || sSiteValue === '' ? 'T' : 'F'}
            >
              <option hidden label={t('Select image')} value="" />
              {sCanfarService?.images
                ?.filter(
                  (image) =>
                    image.types.indexOf(sServiceType) > -1 || sServiceType === '',
                )
                .map((image, index) => (
                  <option key={index} label={image.id} value={image.id} />
                ))}
            </select>
            <label
              id="new-session-session-name-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Session name:
            </label>
            <input
              aria-labelledby="new-session-session-name-label"
              className="input-field"
              type="text"
              name="name"
              onChange={(e) => setName(e.target.value)}
              onKeyPress={alphanumericKeysOnly}
              maxLength={255}
              value={sName}
              placeholder="Session name"
              title="Session name"
            />
          </div>
        </div>

        <div style={{ flex: '0 0 15px' }} />
        <div
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              gap: '5px',
              display: sPage === 'srcnet' ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <label
              id="new-session-duration-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Duration:
            </label>
            <select
              aria-labelledby="new-session-duration-label"
              name="lst-duration-type"
              className="listbox"
              multiple={false}
              value={sDuration}
              onChange={(e) => setDuration(e.target.value)}
              style={{ borderRadius: '0px' }}
            >
              {sessionDurationOptions.map(({ label, value }) => (
                <option key={value} label={label} value={value} />
              ))}
            </select>
            <label
              id="new-session-available-services-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Available services:
            </label>
            <select
              aria-labelledby="new-session-available-services-label"
              name="lst-compute-service"
              className="listbox"
              multiple={false}
              value={sComputeService}
              onChange={(e) => setComputeService(e.target.value)}
              data-placeholder-shown={sComputeService === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%', borderRadius: '0px' }}
              disabled={SERVICE_UUID !== '' || sNodeValue === '' || sSiteValue === ''}
              data-disabled={
                SERVICE_UUID !== '' || sNodeValue === '' || sSiteValue === '' ? 'T' : 'F'
              }
            >
              <option hidden label={t('Select service')} value="" />
              {sD_computeServices
                .filter(
                  (filter) =>
                    filter.site.node === sNodeValue &&
                    (filter.site.site_uuid === sSiteValue || sSiteValue === '') &&
                    (filter.session_type === sServiceType ||
                      (sServiceType === '' &&
                        ['jupyterhub', 'carta'].indexOf(filter.session_type) > -1)),
                )
                .map((service) => (
                  <option
                    key={service.compute_service_id}
                    label={
                      service.service_name
                        ? service.service_name
                        : `${service.session_type.toUpperCase()} (no description)`
                    }
                    value={service.compute_service_id}
                  />
                ))}
            </select>
          </div>

          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              gap: '5px',
              display: sPage === 'canfar' ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <label
              id="new-session-number-cores-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Number of cores:
            </label>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <PleaseWait text="" visible={sLoadingOptions} small={true} />
              <div
                style={{ flex: '0 0 6px', display: sLoadingOptions ? 'flex' : 'none' }}
              />
              <select
                aria-labelledby="new-session-number-cores-label"
                name="lst-cores"
                className="listbox"
                multiple={false}
                value={sCores}
                onChange={(e) => setCores(e.target.value)}
                data-placeholder-shown={sCores === '' ? 'T' : 'F'}
                style={{ flex: '1 1', borderRadius: '0px' }}
                disabled={sNodeValue === '' || sSiteValue === ''}
                data-disabled={sNodeValue === '' || sSiteValue === '' ? 'T' : 'F'}
              >
                <option hidden label={t('Select number of cores')} value="" />
                {sCoreOptions.map((value, index) => (
                  <option key={index} label={value + ' core(s)'} value={value} />
                ))}
              </select>
            </div>
            <label
              id="new-session-ram-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              RAM:
            </label>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <PleaseWait text="" visible={sLoadingOptions} small={true} />
              <div
                style={{ flex: '0 0 6px', display: sLoadingOptions ? 'flex' : 'none' }}
              />
              <select
                aria-labelledby="new-session-ram-label"
                name="lst-ram"
                className="listbox"
                multiple={false}
                value={sRam}
                onChange={(e) => setRam(e.target.value)}
                data-placeholder-shown={sRam === '' ? 'T' : 'F'}
                style={{ flex: '1 1', borderRadius: '0px' }}
                disabled={sNodeValue === '' || sSiteValue === ''}
                data-disabled={sNodeValue === '' || sSiteValue === '' ? 'T' : 'F'}
              >
                <option hidden label={t('Select RAM')} value="" />
                {sRamOptions.map((value, index) => (
                  <option key={index} label={value + ' GB'} value={value} />
                ))}
              </select>
            </div>
            <label
              id="new-session-cmd-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              cmd (headless sessions only):
            </label>
            <input
              aria-labelledby="new-session-cmd-label"
              className="input-field"
              type="text"
              name="cmd"
              onChange={(e) => setCmd(e.target.value)}
              maxLength={255}
              value={sCmd}
              placeholder="Enter cmd"
              title="cmd (headless sessions only)"
              disabled={sServiceType !== 'headless'}
              data-disabled={sServiceType !== 'headless' ? 'T' : 'F'}
            />
            <label
              id="new-session-args-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              args (headless sessions only):
            </label>
            <input
              aria-labelledby="new-session-args-label"
              className="input-field"
              type="text"
              name="args"
              onChange={(e) => setArgs(e.target.value)}
              maxLength={255}
              value={sArgs}
              placeholder="Enter args"
              title="args (headless sessions only)"
              disabled={sServiceType !== 'headless'}
              data-disabled={sServiceType !== 'headless' ? 'T' : 'F'}
            />
            <label
              id="new-session-env-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              env (headless sessions only):
            </label>
            <input
              aria-labelledby="new-session-env-label"
              className="input-field"
              type="text"
              name="env"
              onChange={(e) => setEnv(e.target.value)}
              maxLength={255}
              value={sEnv}
              placeholder="Enter env"
              title="env (headless sessions only)"
              disabled={sServiceType !== 'headless'}
              data-disabled={sServiceType !== 'headless' ? 'T' : 'F'}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          margin: '15px 0px 0px 0px',
        }}
      >
        <div style={{ flex: '0 0 auto' }}>
          <ToolButton
            key={t('Cancel')}
            name="cancel"
            onClick={props.onClose}
            tooltip="Cancel launch compute session modal"
            text={t('Cancel')}
            type={ToolButtonType.SECONDARY}
          />
        </div>
        <div style={{ padding: '5px' }} />
        <div style={{ flex: '0 0 auto' }}>
          <ToolButton
            key={t('Launch')}
            name="launch"
            onClick={handleLaunch}
            tooltip="Launch a compute service"
            text={t('Launch')}
            type={ToolButtonType.PRIMARY}
          />
        </div>
      </div>
    </div>
  );
};

export default NewSessionPanel;
